import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Text from "./shared/Text";

/**
 * DarkAiBrainSection — dark full-bleed section with a green dot eyebrow,
 * split title, description, bulleted selling points, CTA pair, and a
 * closing one-liner in the left column. Right column holds a hero image
 * (photo + product card overlay).
 */

export type DarkAiBrainBullet = { text: string };

export type DarkAiBrainData = {
  eyebrow: string;
  titleMuted: string;
  titleBright: string;
  description: string;
  bullets: DarkAiBrainBullet[];
  ctas?: CtaType[];
  closingLine?: string;
  image?: string;
  imageAlt?: string;
};

const Bullet = ({ text }: DarkAiBrainBullet) => (
  <li className="flex items-start gap-3">
    <FontAwesomeIcon
      icon={faCircleCheck}
      aria-hidden="true"
      className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[#5766F2]"
    />
    <Text
      as="span"
      variant="body-md"
      className="text-[14px] leading-snug text-white md:text-[15px]"
    >
      {text}
    </Text>
  </li>
);

const DarkAiBrainSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  description,
  bullets,
  ctas,
  closingLine,
  image,
  imageAlt,
}: DarkAiBrainData) => {
  return (
    <section className="relative w-full overflow-hidden bg-neutralBlack">
      {/* Ambient glow on the far right — matches the reference's subtle
          warm/violet bloom around the image. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-1/2"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 100% 50%, rgba(120, 80, 160, 0.35) 0%, rgba(120, 80, 160, 0.1) 45%, rgba(0, 0, 0, 0) 75%)",
        }}
      />

      <div className="container relative z-10 mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
          {/* LEFT */}
          <div>
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rounded-full bg-[#D4F75E]"
              />
              <Text
                variant="eyebrow"
                className="text-[12px] font-semibold tracking-[0.14em] text-[#D4F75E] md:text-[13px]"
              >
                {eyebrow}
              </Text>
            </div>

            <Text
              as="h2"
              variant="display-lg"
              className="mt-4 text-[28px] sm:text-[34px] md:text-[40px] lg:text-[48px]"
            >
              <span className="text-white">{titleMuted}</span>{" "}
              <span className="text-white">{titleBright}</span>
            </Text>

            <Text
              as="p"
              variant="body-lg"
              className="mt-5 max-w-xl text-[14px] leading-relaxed text-white/70 md:text-[16px]"
            >
              {description}
            </Text>

            <ul className="mt-8 flex flex-col gap-4 md:mt-10">
              {bullets.map((b) => (
                <Bullet key={b.text} text={b.text} />
              ))}
            </ul>

            {ctas && ctas.length ? (
              <div className="mt-8 md:mt-10">
                <CtaButtonGroup
                  ctas={ctas}
                  gap="flex flex-wrap items-center gap-3"
                  fullWidthOnMobile={false}
                />
              </div>
            ) : null}

            {closingLine ? (
              <Text
                as="p"
                variant="body-md"
                className="mt-6 max-w-md text-[13px] leading-relaxed text-white/60 md:text-[14px]"
              >
                {closingLine}
              </Text>
            ) : null}
          </div>

          {/* RIGHT */}
          <div className="flex items-center justify-center lg:justify-end">
            {image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={image}
                alt={imageAlt ?? ""}
                className="block w-full max-w-[560px] rounded-3xl"
              />
            ) : (
              <div className="aspect-[4/3] w-full max-w-[560px] rounded-3xl bg-gradient-to-br from-white/10 to-white/[0.02]" />
            )}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default DarkAiBrainSection;
